import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

// Panel genérico de historial de cambios (tabla `sync_history` del backend).
// Se usa tanto para /admin/alumnos/historial como /admin/pisos/historial,
// pasando `endpoint` y textos distintos. Filtrado/orden/paginación se
// resuelven en el servidor (la tabla de pisos en producción tiene >80.000
// filas, no es viable traerlas todas de golpe).

const EVENTO_INFO = {
  TODOS: { label: "Todos", emoji: "🗂️", bg: "#eef1f5", color: "#37474f", border: "#cfd8dc" },
  ALTA: { label: "Alta", emoji: "🆕", bg: "#e6f4ea", color: "#1e7e34", border: "#b7e1c1" },
  BAJA: { label: "Baja", emoji: "🚫", bg: "#fdecea", color: "#c0392b", border: "#f3c2bd" },
  MODIFICACION: { label: "Modificado", emoji: "✏️", bg: "#fff8e1", color: "#8a6d00", border: "#f0e2a3" },
};

const PER_PAGE_OPCIONES = [25, 50, 100, 200];

// Etiquetas legibles para las claves más comunes de los snapshots JSON de
// alumnos/pisos. Las que no aparecen aquí se humanizan automáticamente
// (guiones bajos -> espacios + mayúscula inicial).
const ETIQUETAS_CAMPO = {
  id_alumno: "ID alumno", id_piso: "Piso", habitacion: "Habitación", duracion: "Duración",
  pais: "País", a_r: "A/R", activo: "Activo", fecha_baja: "Fecha de baja",
  abreviatura: "Abreviatura", plazas_ocupadas: "Plazas ocupadas", empresa: "Empresa",
  sexo: "Sexo", servicio: "Servicio", horas_dia: "Horas/día", horas_semana: "Horas/semana",
  user_supervisora: "Supervisora", user_comprador: "Comprador", id_asistenta: "ID asistenta",
  nombre_asistenta: "Asistenta", ruta: "Ruta", supermercado: "Supermercado",
  franja_horaria: "Franja horaria", nombre: "Nombre",
  user: "Usuario", password: "Contraseña", apellidos: "Apellidos", telefono: "Teléfono", correo: "Correo",
  tipo: "Tipo", id_empleado: "ID empleado",
};

function humanizarClave(k) {
  if (ETIQUETAS_CAMPO[k]) return ETIQUETAS_CAMPO[k];
  return k.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
}

function humanizarValor(v) {
  if (v === null || v === undefined || v === "") return "—";
  return String(v);
}

function EventoBadge({ tipo }) {
  const info = EVENTO_INFO[tipo] || { label: tipo, emoji: "•", bg: "#eee", color: "#333", border: "#ddd" };
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "4px 10px",
        borderRadius: 999,
        fontSize: 13,
        fontWeight: 700,
        background: info.bg,
        color: info.color,
        border: `1px solid ${info.border}`,
        whiteSpace: "nowrap",
      }}
    >
      <span aria-hidden="true">{info.emoji}</span>
      {info.label}
    </span>
  );
}

function formatFecha(iso) {
  if (!iso) return "-";
  try {
    const d = new Date(iso);
    return d.toLocaleString("es-ES", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch (_) {
    return iso;
  }
}

// valor_anterior/valor_nuevo de ALTA y BAJA son snapshots JSON de la fila
// completa; para MODIFICACION son el valor plano del campo cambiado.
function resumenValor(valor) {
  if (valor === null || valor === undefined || valor === "") return "-";
  const s = String(valor);
  if (s.length > 60) return s.slice(0, 57) + "…";
  return s;
}

// Panel lateral deslizante que muestra el snapshot completo (ALTA/BAJA) de
// forma legible (clave: valor), en vez de JSON en crudo.
function PanelSnapshot({ abierto, titulo, subtitulo, json, onCerrar }) {
  useEffect(() => {
    if (!abierto) return;
    const onKey = (e) => { if (e.key === "Escape") onCerrar(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [abierto, onCerrar]);

  if (!abierto) return null;

  let campos = null;
  try {
    const obj = JSON.parse(json);
    campos = Object.entries(obj);
  } catch (_) {
    campos = null;
  }

  return (
    <div style={styles.overlay} onClick={onCerrar}>
      <div style={styles.panelLateral} onClick={(e) => e.stopPropagation()}>
        <div style={styles.panelHeader}>
          <div>
            <h3 style={styles.panelTitulo}>{titulo}</h3>
            {subtitulo && <p style={styles.panelSubtitulo}>{subtitulo}</p>}
          </div>
          <button style={styles.panelCerrar} onClick={onCerrar} aria-label="Cerrar" title="Cerrar (Esc)">✕</button>
        </div>
        <div style={styles.panelBody}>
          {campos === null ? (
            <pre style={styles.panelPre}>{json}</pre>
          ) : (
            <table style={styles.panelTabla}>
              <tbody>
                {campos.map(([k, v]) => (
                  <tr key={k}>
                    <td style={styles.panelClave}>{humanizarClave(k)}</td>
                    <td style={styles.panelValor}>{k === "password" ? "🔒 (hash oculto)" : humanizarValor(v)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

const HistorialCambios = ({ endpoint, titulo, entidadLabel, idLabel = "ID", buscarPlaceholder, volverA, sugerenciasEndpoint, sugerenciasCampo }) => {
  const navigate = useNavigate();
  const [filtros, setFiltros] = useState({ tipo_evento: "", q: "", fila_id: "", desde: "", hasta: "" });
  const [orden, setOrden] = useState("fecha_hora");
  const [dir, setDir] = useState("desc");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [data, setData] = useState({ rows: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sugerencias, setSugerencias] = useState([]);
  const [panel, setPanel] = useState(null); // { titulo, subtitulo, json } | null
  const datalistId = useRef(`sugerencias-${Math.random().toString(36).slice(2)}`).current;

  const cargar = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = { ...filtros, orden, dir, page, per_page: perPage };
      Object.keys(params).forEach((k) => { if (params[k] === "") delete params[k]; });
      const res = await axios.get(endpoint, { params });
      setData(res.data);
    } catch (e) {
      setError("❌ No se pudo cargar el historial.");
    } finally {
      setLoading(false);
    }
  }, [endpoint, filtros, orden, dir, page, perPage]);

  useEffect(() => {
    const t = setTimeout(cargar, 300); // debounce (sobre todo para el buscador de texto)
    return () => clearTimeout(t);
  }, [cargar]);

  // Lista de nombres/abreviaturas existentes para autocompletar el buscador.
  useEffect(() => {
    if (!sugerenciasEndpoint) return;
    axios.get(sugerenciasEndpoint)
      .then((res) => {
        // La mayoría de estos endpoints devuelven un array plano, pero algunos
        // (p.ej. /usuarios) lo envuelven en {success, data}.
        const lista = Array.isArray(res.data) ? res.data : Array.isArray(res.data?.data) ? res.data.data : [];
        const valores = lista.map((it) => it[sugerenciasCampo]).filter(Boolean);
        setSugerencias([...new Set(valores)].sort());
      })
      .catch(() => setSugerencias([]));
  }, [sugerenciasEndpoint, sugerenciasCampo]);

  const handleFiltroChange = (e) => {
    const { name, value } = e.target;
    setPage(1);
    setFiltros((prev) => ({ ...prev, [name]: value }));
  };

  const setEvento = (valor) => {
    setPage(1);
    setFiltros((prev) => ({ ...prev, tipo_evento: valor }));
  };

  const limpiarFiltros = () => {
    setPage(1);
    setFiltros({ tipo_evento: "", q: "", fila_id: "", desde: "", hasta: "" });
  };

  const hayFiltrosActivos = Object.values(filtros).some((v) => v !== "");

  const toggleOrden = (campo) => {
    setPage(1);
    if (orden === campo) {
      setDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setOrden(campo);
      setDir("desc");
    }
  };

  const flecha = (campo) => (orden === campo ? (dir === "asc" ? " ▲" : " ▼") : "");

  const totalPaginas = Math.max(1, Math.ceil((data.total || 0) / perPage));

  const abrirPanel = (r, cual) => {
    const json = cual === "nuevo" ? r.valor_nuevo : r.valor_anterior;
    if (!json) return;
    const esAlta = r.tipo_evento === "ALTA";
    setPanel({
      titulo: `${esAlta ? "🆕 Alta" : "🚫 Baja"} · ${r.identificador_snapshot || `#${r.fila_id}`}`,
      subtitulo: `${formatFecha(r.fecha_hora)} · ${r.editor_email || "desconocido"}`,
      json,
    });
  };

  return (
    <div style={styles.container}>
      <div style={styles.headerRow}>
        <h2 style={styles.title}>📜 {titulo}</h2>
        <button style={styles.volver} onClick={() => navigate(volverA)}>← Volver</button>
      </div>

      <div style={styles.filtrosContainer}>
        <div style={styles.filtrosFila}>
          <div style={styles.campoGrupo}>
            <label style={styles.campoLabel}>Buscar</label>
            <input
              type="text"
              name="q"
              list={datalistId}
              placeholder={buscarPlaceholder || `Nombre de ${entidadLabel.toLowerCase()}, editor...`}
              value={filtros.q}
              onChange={handleFiltroChange}
              style={{ ...styles.input, width: 240 }}
            />
            {sugerencias.length > 0 && (
              <datalist id={datalistId}>
                {sugerencias.map((s) => <option key={s} value={s} />)}
              </datalist>
            )}
          </div>

          <div style={styles.campoGrupo}>
            <label style={styles.campoLabel}>ID / usuario exacto</label>
            <input
              type="text"
              name="fila_id"
              placeholder="Ej: 123 o usuario"
              value={filtros.fila_id}
              onChange={handleFiltroChange}
              style={{ ...styles.input, width: 120 }}
            />
          </div>

          <div style={styles.campoGrupo}>
            <label style={styles.campoLabel}>Desde</label>
            <input type="date" name="desde" value={filtros.desde} onChange={handleFiltroChange} style={styles.input} />
          </div>
          <div style={styles.campoGrupo}>
            <label style={styles.campoLabel}>Hasta</label>
            <input type="date" name="hasta" value={filtros.hasta} onChange={handleFiltroChange} style={styles.input} />
          </div>

          <div style={styles.campoGrupo}>
            <label style={styles.campoLabel}>Por página</label>
            <select
              value={perPage}
              onChange={(e) => { setPage(1); setPerPage(Number(e.target.value)); }}
              style={styles.select}
            >
              {PER_PAGE_OPCIONES.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>

          {hayFiltrosActivos && (
            <button style={styles.clearFilters} onClick={limpiarFiltros}>❌ Quitar filtros</button>
          )}
        </div>

        <div style={styles.pillsFila}>
          {["", "ALTA", "MODIFICACION", "BAJA"].map((valor) => {
            const info = EVENTO_INFO[valor || "TODOS"];
            const activo = filtros.tipo_evento === valor;
            return (
              <button
                key={valor || "todos"}
                onClick={() => setEvento(valor)}
                style={{
                  ...styles.pill,
                  background: activo ? info.color : info.bg,
                  color: activo ? "#fff" : info.color,
                  border: `1px solid ${activo ? info.color : info.border}`,
                }}
              >
                {info.emoji} {info.label}
              </button>
            );
          })}
        </div>
      </div>

      {loading ? (
        <p style={{ textAlign: "center", marginTop: 30 }}>Cargando historial...</p>
      ) : error ? (
        <p style={{ color: "red", textAlign: "center", marginTop: 30 }}>{error}</p>
      ) : data.rows.length === 0 ? (
        <p style={{ textAlign: "center", marginTop: 30 }}>No hay eventos que coincidan con los filtros.</p>
      ) : (
        <>
          <div style={{ overflowX: "auto" }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.headerCell} onClick={() => toggleOrden("fecha_hora")}>Fecha{flecha("fecha_hora")}</th>
                  <th style={styles.headerCell} onClick={() => toggleOrden("tipo_evento")}>Evento{flecha("tipo_evento")}</th>
                  <th style={styles.headerCell} onClick={() => toggleOrden("fila_id")}>{idLabel}{flecha("fila_id")}</th>
                  <th style={styles.headerCell}>{entidadLabel}</th>
                  <th style={styles.headerCell}>Campo</th>
                  <th style={styles.headerCell}>Antes</th>
                  <th style={styles.headerCell}>Después</th>
                  <th style={styles.headerCell} onClick={() => toggleOrden("editor_email")}>Editor{flecha("editor_email")}</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.id} style={styles.row}>
                    <td style={styles.cell}>{formatFecha(r.fecha_hora)}</td>
                    <td style={styles.cell}><EventoBadge tipo={r.tipo_evento} /></td>
                    <td style={styles.cell}>{r.fila_id}</td>
                    <td style={styles.cell}><strong>{r.identificador_snapshot || "-"}</strong></td>
                    {r.tipo_evento === "MODIFICACION" ? (
                      r.campo === "password" ? (
                        <>
                          <td style={styles.cell}>{humanizarClave("password")}</td>
                          <td style={styles.cell} colSpan={2}>🔒 Contraseña cambiada (hash guardado, nunca en texto plano)</td>
                        </>
                      ) : (
                      <>
                        <td style={styles.cell}>{r.campo || "-"}</td>
                        <td style={styles.cell}>{resumenValor(r.valor_anterior)}</td>
                        <td style={styles.cell}>{resumenValor(r.valor_nuevo)}</td>
                      </>
                      )
                    ) : r.tipo_evento === "ALTA" ? (
                      <>
                        <td style={styles.cell}>-</td>
                        <td style={styles.cell}>-</td>
                        <td style={styles.cell}>
                          {r.valor_nuevo ? (
                            <button style={styles.verDatos} onClick={() => abrirPanel(r, "nuevo")}>👁️ Ver datos</button>
                          ) : "-"}
                        </td>
                      </>
                    ) : (
                      <>
                        <td style={styles.cell}>-</td>
                        <td style={styles.cell}>
                          {r.valor_anterior ? (
                            <button style={styles.verDatos} onClick={() => abrirPanel(r, "anterior")}>👁️ Ver datos</button>
                          ) : "-"}
                        </td>
                        <td style={styles.cell}>-</td>
                      </>
                    )}
                    <td style={styles.cell}>{r.editor_email || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={styles.paginacion}>
            <button
              style={styles.pagBtn}
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              ← Anterior
            </button>
            <span style={styles.pagInfo}>
              Página {page} de {totalPaginas} · {data.total} evento{data.total === 1 ? "" : "s"}
            </span>
            <button
              style={styles.pagBtn}
              disabled={page >= totalPaginas}
              onClick={() => setPage((p) => Math.min(totalPaginas, p + 1))}
            >
              Siguiente →
            </button>
          </div>
        </>
      )}

      <PanelSnapshot
        abierto={!!panel}
        titulo={panel?.titulo}
        subtitulo={panel?.subtitulo}
        json={panel?.json}
        onCerrar={() => setPanel(null)}
      />
    </div>
  );
};

const styles = {
  container: { padding: "24px", maxWidth: 1200, margin: "0 auto" },
  headerRow: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18, flexWrap: "wrap", gap: 10 },
  title: { color: "#2c3e50", margin: 0 },
  volver: {
    padding: "10px 18px", backgroundColor: "#607d8b", color: "#fff", border: "none",
    borderRadius: 8, cursor: "pointer", fontWeight: 600,
  },
  filtrosContainer: {
    display: "flex", flexDirection: "column", gap: 14,
    marginBottom: 20, padding: 16, background: "#f7f9fa", borderRadius: 12, border: "1px solid #e5e7eb",
  },
  filtrosFila: { display: "flex", flexWrap: "wrap", gap: 14, alignItems: "flex-end" },
  campoGrupo: { display: "flex", flexDirection: "column", gap: 4 },
  campoLabel: { fontSize: 11, fontWeight: 700, color: "#78909c", textTransform: "uppercase", letterSpacing: 0.4 },
  input: {
    padding: "9px 12px", fontSize: 14, border: "1px solid #ccc", borderRadius: 8,
  },
  select: {
    padding: "9px 12px", fontSize: 14, border: "1px solid #ccc", borderRadius: 8, cursor: "pointer",
  },
  clearFilters: {
    padding: "9px 14px", backgroundColor: "#fff", border: "1px solid #e57373", color: "#c0392b",
    borderRadius: 8, cursor: "pointer", fontWeight: 600, height: 38,
  },
  pillsFila: { display: "flex", gap: 8, flexWrap: "wrap" },
  pill: {
    padding: "8px 16px", borderRadius: 999, cursor: "pointer", fontWeight: 700, fontSize: 13,
    transition: "transform 0.12s ease, box-shadow 0.12s ease",
  },
  table: { width: "100%", borderCollapse: "collapse", background: "#fff", boxShadow: "0 1px 4px rgba(0,0,0,0.08)" },
  headerCell: {
    padding: "12px", textAlign: "center", background: "#2c3e50", color: "#fff",
    fontWeight: 700, fontSize: 13, cursor: "pointer", userSelect: "none", whiteSpace: "nowrap",
  },
  row: { borderBottom: "1px solid #eee" },
  cell: { padding: "10px 12px", textAlign: "center", fontSize: 13, color: "#34495e", verticalAlign: "top" },
  verDatos: {
    border: "1px solid #90caf9", background: "#e3f2fd", color: "#1565c0", cursor: "pointer",
    fontSize: 12, fontWeight: 700, padding: "5px 10px", borderRadius: 999,
  },
  paginacion: {
    display: "flex", justifyContent: "center", alignItems: "center", gap: 16, marginTop: 18,
  },
  pagBtn: {
    padding: "8px 16px", borderRadius: 8, border: "1px solid #ccc", background: "#fff", cursor: "pointer", fontWeight: 600,
  },
  pagInfo: { fontSize: 14, color: "#555" },

  // Panel lateral (drawer) para ver snapshots de ALTA/BAJA
  overlay: {
    position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
    background: "rgba(15, 23, 42, 0.45)", zIndex: 1000,
    display: "flex", justifyContent: "flex-end",
  },
  panelLateral: {
    width: "min(420px, 92vw)", height: "100%", background: "#fff",
    boxShadow: "-8px 0 24px rgba(0,0,0,0.25)", display: "flex", flexDirection: "column",
  },
  panelHeader: {
    display: "flex", justifyContent: "space-between", alignItems: "flex-start",
    padding: "18px 20px", borderBottom: "1px solid #eee", background: "#f7f9fa",
  },
  panelTitulo: { margin: 0, fontSize: 17, color: "#263238" },
  panelSubtitulo: { margin: "4px 0 0", fontSize: 12, color: "#78909c" },
  panelCerrar: {
    border: "none", background: "#eceff1", color: "#455a64", width: 32, height: 32,
    borderRadius: "50%", cursor: "pointer", fontSize: 15, fontWeight: 700, flexShrink: 0,
  },
  panelBody: { padding: 20, overflowY: "auto" },
  panelTabla: { width: "100%", borderCollapse: "collapse" },
  panelClave: {
    padding: "8px 10px", fontSize: 12, fontWeight: 700, color: "#607d8b",
    borderBottom: "1px solid #f0f0f0", verticalAlign: "top", whiteSpace: "nowrap",
  },
  panelValor: {
    padding: "8px 10px", fontSize: 13, color: "#263238", borderBottom: "1px solid #f0f0f0",
    wordBreak: "break-word",
  },
  panelPre: {
    background: "#f7f7f9", border: "1px solid #e1e1e1", borderRadius: 8, padding: 12, fontSize: 12,
    overflowX: "auto",
  },
};

export default HistorialCambios;
